import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { SidebarContent } from './SidebarContent';

interface MobileNavProps {
  brand: { logoUrl?: string | null; company?: string | null };
}

/**
 * Hamburger + slide-in drawer used below the `lg` breakpoint, where the
 * docked Sidebar / TopBar are hidden. Reuses SidebarContent so the nav
 * items, ACL filtering and module search stay identical to desktop.
 * Closes itself whenever the route changes.
 */
export function MobileNav({ brand }: MobileNavProps) {
  const [open, setOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();
  const showLogo = Boolean(brand.logoUrl) && !logoError;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open navigation menu"
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="left"
          className="flex w-72 max-w-[85vw] flex-col gap-0 bg-sidebar p-0 text-sidebar-foreground [&>button]:text-sidebar-foreground lg:hidden"
        >
          <div className="flex h-14 shrink-0 items-center gap-2 border-b border-sidebar-border px-3 pr-10">
            {showLogo ? (
              <img
                src={brand.logoUrl as string}
                alt={brand.company ?? 'Logo'}
                className="h-7 w-7 shrink-0 rounded-full border border-white/20 object-cover"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="flex h-7 w-7 items-center justify-center rounded bg-sidebar-accent text-xs font-bold uppercase tracking-tight text-sidebar-foreground">
                {(brand.company ?? 'EI').slice(0, 2)}
              </div>
            )}
            <SheetTitle className="truncate text-sm font-semibold tracking-tight text-sidebar-foreground">
              {brand.company ?? 'Express IVR'}
            </SheetTitle>
          </div>
          <nav className="flex-1 overflow-y-auto pb-3 pl-2 pr-0">
            <SidebarContent />
          </nav>
        </SheetContent>
      </Sheet>
    </>
  );
}
